import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Repeat } from "lucide-react";
import { api, errMsg, fmt } from "@/lib/api";
import { SlotPicker } from "@/components/SlotPicker";

const fmtSlot = (iso) => fmt(iso, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

const SeriesRow = ({ s, onCancelled }) => {
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    if (!window.confirm(`Cancel the remaining ${s.remaining} ${s.weekday} ${s.time} sessions? Anything inside 24h stays.`)) return;
    setBusy(true);
    try { const r = await api.delete(`/client/me/series/${s.seriesId}`); toast.success(`${r.data.cancelled} sessions cancelled`); onCancelled(); }
    catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <div className="sess" data-testid={`series-row-${s.seriesId}`}>
      <span className="chip ok" data-testid={`series-chip-${s.seriesId}`}><Repeat size={12} style={{ marginRight: 4 }} />{s.weekday}s {s.time}</span>
      <div className="sb"><div className="ss">Your weekly slot · {s.remaining} left · next {fmtSlot(s.nextAt)}</div></div>
      <button className="btn sm g" disabled={busy} onClick={cancel} data-testid={`series-cancel-${s.seriesId}`}>{busy ? "Cancelling…" : "Cancel the rest"}</button>
    </div>
  );
};

export const BookSession = ({ onBooked }) => {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);
  const [slot, setSlot] = useState(null);
  const [focus, setFocus] = useState("");
  const [repeat, setRepeat] = useState(false);
  const [weeks, setWeeks] = useState(8);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const load = useCallback(() => api.get("/client/me/booking").then((r) => setD(r.data)).catch(() => setD(false)), []);
  useEffect(() => { load(); }, [load]);
  if (d === null || d === false || d.selfBook === false) return null;
  const maxW = d.selfBookMaxSeriesWeeks || 12;
  const weekOptions = [4, 8, 12, 16, 26].filter((w) => w <= maxW);
  if (!weekOptions.includes(maxW)) weekOptions.push(maxW);
  const reset = () => { setOpen(false); setSlot(null); setFocus(""); setRepeat(false); };
  const book = async () => {
    setBusy(true);
    try {
      const r = await api.post("/client/me/book", { startAt: slot, focus: focus || null, repeatWeeks: repeat ? Math.min(weeks, maxW) : null });
      if (repeat) { setResult(r.data); toast.success(`Locked in ${r.data.label} · ${r.data.booked.length} sessions`); }
      else toast.success(`Booked ✓ ${fmtSlot(r.data.startAt)}`);
      reset(); load(); onBooked?.();
    } catch (ex) { toast.error(errMsg(ex)); load(); } finally { setBusy(false); }
  };
  const confirmLabel = !slot ? "Pick a slot" : repeat ? `Lock in ${fmt(slot, { weekday: "long" })}s ${fmt(slot, { hour: "2-digit", minute: "2-digit" })} × ${Math.min(weeks, maxW)}` : `Confirm ${fmt(slot, { weekday: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}`;
  return (
    <div className="panel" data-testid="book-session-card">
      <div className="ph">
        <h3>Book a session · pick any free slot</h3>
        {d.canBook && !open && <button className="btn sm" onClick={() => { setResult(null); setOpen(true); }} data-testid="book-session-open">+ Book</button>}
      </div>
      <div className="ss" data-testid="book-session-meta">
        {d.credits.bought > 0 ? `${d.credits.remaining} credit${d.credits.remaining !== 1 ? "s" : ""} left` : "No pack needed yet"} · {d.activeBookings}/{d.selfBookMaxUpcoming} bookings upcoming{d.selfBookRecurring ? " (a weekly series counts as one)" : ""} · {d.sessionMin} min · {d.selfBookNoticeHours}h notice
      </div>
      {d.series.map((s) => <SeriesRow key={s.seriesId} s={s} onCancelled={() => { load(); onBooked?.(); }} />)}
      {result && (
        <div className="msg" style={{ marginTop: 10 }} data-testid="series-result">
          <div className="who"><span>Weekly slot locked in</span></div>
          <div className="txt">{result.label} · {result.booked.length} of {result.weeks} weeks booked{result.cappedByCredits ? " (capped at your remaining credits)" : ""}.
            {result.skipped.length > 0 && <> Couldn't fit <b data-testid="series-skipped-count">{result.skipped.length}</b>: {result.skipped.map((x) => fmt(x, { day: "numeric", month: "short" })).join(", ")} — book those separately.</>}</div>
        </div>
      )}
      {!d.canBook && <div className="msg" style={{ marginTop: 10 }} data-testid="book-session-blocked"><div className="who"><span>Booking paused</span></div><div className="txt">{d.reason}</div></div>}
      {open && d.canBook && (
        <div style={{ marginTop: 12 }} data-testid="book-session-form">
          <SlotPicker data={d.days} value={slot} onChange={setSlot} />
          {slot && d.selfBookRecurring && (
            <div className="msg" style={{ marginTop: 10 }} data-testid="repeat-box">
              <label className="chk" style={{ display: "flex", alignItems: "center", gap: 8, margin: 0 }}>
                <input type="checkbox" checked={repeat} onChange={(e) => setRepeat(e.target.checked)} style={{ width: "auto" }} data-testid="repeat-weekly-toggle" />
                <Repeat size={14} /> Repeat weekly — same {fmt(slot, { weekday: "long" })} {fmt(slot, { hour: "2-digit", minute: "2-digit" })} every week
              </label>
              {repeat && (
                <div className="tabs" style={{ marginTop: 8 }} data-testid="repeat-weeks">
                  {weekOptions.map((w) => <button type="button" key={w} className={Math.min(weeks, maxW) === w ? "on" : ""} onClick={() => setWeeks(w)} data-testid={`repeat-weeks-${w}`}>{w} wks</button>)}
                </div>
              )}
              {repeat && <div className="ss" style={{ marginTop: 6 }}>Weeks where this slot is taken get skipped and listed so you can book them separately.{d.selfBookRequiresCredit && d.credits.bought > 0 ? ` Capped at your ${d.credits.remaining} remaining credits.` : ""}</div>}
            </div>
          )}
          <label style={{ marginTop: 10 }}>What do you want to work on? (optional)</label>
          <input value={focus} onChange={(e) => setFocus(e.target.value)} maxLength={80} placeholder="Upper body, technique check, conditioning…" data-testid="book-session-focus-input" />
          <div className="mfoot">
            <button type="button" className="btn g sm" onClick={reset} data-testid="book-session-cancel">Cancel</button>
            <button type="button" className="btn sm" disabled={!slot || busy} onClick={book} data-testid="book-session-confirm">{busy ? "Booking…" : confirmLabel}</button>
          </div>
        </div>
      )}
    </div>
  );
};
